/**
 * Estado de sincronizacao no cabecalho.
 *
 * Ela nunca pode ficar sem saber se o que escreveu esta salvo. Por isso o
 * indicador diz sempre a verdade em palavras — nunca so por cor (ADR-0007):
 * icone + palavra + numero.
 *
 * "Salvo no celular" e diferente de "Salvo e enviado", e ambos sao
 * diferentes de "Por rever". A distincao importa-lhe.
 */
import type { SyncStatus } from '@flos/shared';

export interface SyncIndicatorProps {
  status: SyncStatus;
  onOpenConflicts?: () => void;
}

interface Descriptor {
  icon: string;
  word: string;
  detail: string;
  tone: 'ok' | 'wait' | 'warn';
}

export function describeSync(status: SyncStatus): Descriptor {
  if (status.conflicts > 0) {
    return {
      icon: '⚠️',
      word: 'Por rever',
      detail:
        status.conflicts === 1
          ? '1 alteração precisa da sua decisão'
          : `${status.conflicts} alterações precisam da sua decisão`,
      tone: 'warn',
    };
  }
  if (!status.online) {
    return {
      icon: '📴',
      word: 'Sem internet',
      detail:
        status.pending > 0
          ? `${status.pending} por enviar — está tudo salvo no aparelho`
          : 'Está tudo salvo no aparelho',
      tone: 'wait',
    };
  }
  if (status.pending > 0) {
    return {
      icon: '⬆️',
      word: 'A enviar',
      detail: status.pending === 1 ? '1 por enviar' : `${status.pending} por enviar`,
      tone: 'wait',
    };
  }
  if (status.phase === 'error') {
    return {
      icon: '⚠️',
      word: 'Sem enviar',
      detail: 'Está salvo aqui. Vou tentar outra vez.',
      tone: 'warn',
    };
  }
  return { icon: '✅', word: 'Salvo', detail: 'Tudo enviado', tone: 'ok' };
}

export function SyncIndicator({ status, onOpenConflicts }: SyncIndicatorProps) {
  const descriptor = describeSync(status);
  const clickable = status.conflicts > 0 && onOpenConflicts !== undefined;

  const content = (
    <>
      <span className="flos-icon" aria-hidden="true">
        {descriptor.icon}
      </span>
      <span className="app-sync__word">{descriptor.word}</span>
      <span className="flos-sr-only">. {descriptor.detail}</span>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className={`app-sync app-sync--${descriptor.tone} app-sync--clickable`}
        onClick={onOpenConflicts}
        aria-label={`${descriptor.word}. ${descriptor.detail}. Tocar para rever.`}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      className={`app-sync app-sync--${descriptor.tone}`}
      role="status"
      aria-label={`${descriptor.word}. ${descriptor.detail}`}
      title={descriptor.detail}
    >
      {content}
    </span>
  );
}
